// ===============================
// src/pages/CreditoPrepagato.jsx
// ===============================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getProfilo,
  getMyWallet,
  createWalletTopupSession,
  getWalletTopupOffers,
} from "../Admin/adminApi";
import "./css/CreditoPrepagato.css";

export default function CreditoPrepagato() {
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const [walletLoading, setWalletLoading] = useState(false);
  const [wallet, setWallet] = useState({
    balance_cents: 0,
    transactions: [],
  });

  const [topupAmount, setTopupAmount] = useState("20");
  const [offersLoading, setOffersLoading] = useState(false);
  const [topupOffers, setTopupOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [topupBusy, setTopupBusy] = useState(false);

  const role = String(me?.role || "").toLowerCase();
  const isFornitore = role === "fornitore";

  const fmtMoney = (cents) =>
    (Number(cents || 0) / 100).toLocaleString("it-IT", {
      style: "currency",
      currency: "EUR",
    });

  const displayName = useMemo(() => {
    if (!me) return "";
    return (
      [me.name, me.last_name].filter(Boolean).join(" ") || me.email || "Cliente"
    );
  }, [me]);

  const reloadWallet = useCallback(async () => {
    try {
      setWalletLoading(true);
      const data = await getMyWallet(token);

      setWallet({
        balance_cents: Number(data.balance_cents || 0),
        transactions: Array.isArray(data.transactions) ? data.transactions : [],
      });
    } catch (e) {
      console.error("reloadWallet failed:", e);
      toast.error("Errore caricamento saldo");
      setWallet({ balance_cents: 0, transactions: [] });
    } finally {
      setWalletLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        const profile = await getProfilo(token);
        if (!alive) return;

        setMe(profile);

        if (String(profile?.role || "").toLowerCase() !== "fornitore") {
          await reloadWallet();
        }
      } catch (e) {
        console.error(e);
        toast.error("Errore caricamento credito prepagato");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [token, reloadWallet]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setOffersLoading(true);
        const data = await getWalletTopupOffers(token);
        if (!alive) return;
        setTopupOffers(Array.isArray(data?.offers) ? data.offers : []);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setTopupOffers([]);
      } finally {
        if (alive) setOffersLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [token]);

  const onTopupWallet = async (e) => {
    e.preventDefault();

    const offerIdNum = selectedOfferId ? Number(selectedOfferId) : null;

    if (offerIdNum) {
      try {
        setTopupBusy(true);
        const { url } = await createWalletTopupSession(null, token, {
          offerId: offerIdNum,
        });
        window.location.href = url;
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Errore avvio ricarica");
      } finally {
        setTopupBusy(false);
      }
      return;
    }

    const amount = Number(topupAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Importo non valido");
      return;
    }

    try {
      setTopupBusy(true);
      const { url } = await createWalletTopupSession(amount, token);
      window.location.href = url;
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Errore avvio ricarica");
    } finally {
      setTopupBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="cp-page">
        <div className="cp-skel cp-skel--hero" />
        <div className="cp-skel" />
        <div className="cp-skel" />
      </main>
    );
  }

  if (isFornitore) {
    return (
      <main className="cp-page">
        <section className="cp-card cp-card--notice">
          <h1>Credito prepagato</h1>
          <p>Questa sezione è disponibile solo per i clienti.</p>
          <Link className="cp-back-link" to="/profilo">
            Torna al profilo
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cp-page">
      <section className="cp-hero" aria-label="Credito prepagato Stirup">
        <img src="/credito_prepagato.jpg" alt="Credito prepagato Stirup" />
      </section>

      <section className="cp-card cp-intro-card">
        <h1>Credito prepagato</h1>

        <p>
          Ricarica il tuo credito Stirup e usalo quando vuoi per pagare i tuoi
          ordini di stiratura.
          <br />
          È il modo più semplice per tenere tutto sotto controllo: scegli
          l’importo, ricarichi una volta e utilizzi il saldo disponibile per le
          prossime prenotazioni.
          <br />
          Il credito resta nel tuo account, viene scalato automaticamente al
          momento del pagamento e puoi utilizzarlo entro 12 mesi.
        </p>

        <p>Scegli quanto ricaricare, più ricarichi più bonus hai!</p>

        <p className="cp-balance-line">
          Il tuo saldo disponibile è{" "}
          {walletLoading ? "···" : fmtMoney(wallet.balance_cents)}
        </p>
      </section>

      <section className="cp-card cp-topup-card">
        <p className="cp-topup-copy">
          Indica l’importo che vuoi ricaricare oppure seleziona uno dei tagli di
          ricarica che danno diritto ai bonus
        </p>

        <form onSubmit={onTopupWallet} className="cp-topup-form">
          <div className="cp-topup-row">
            <label className="cp-field cp-field--amount">
              <span>Euro</span>
              <input
                type="number"
                min="10"
                max="300"
                step="5"
                value={topupAmount}
                onChange={(e) => {
                  setTopupAmount(e.target.value);
                  if (selectedOfferId) setSelectedOfferId("");
                }}
                disabled={!!selectedOfferId}
                inputMode="decimal"
                aria-label="Euro da ricaricare"
              />
            </label>

            <span className="cp-or">oppure</span>

            <label className="cp-field cp-field--promo">
              <span>Scegli la promo di ricarica</span>
              <select
                value={selectedOfferId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedOfferId(v);
                  if (v) setTopupAmount("");
                  else if (!topupAmount) setTopupAmount("20");
                }}
                disabled={offersLoading}
                aria-label="Scegli la promo di ricarica"
              >
                <option value="">Nessuna promo</option>
                {topupOffers.map((o) => {
                  const base = Number(o.base_amount_eur || 0);
                  const bonus = Number(o.bonus_eur || 0);
                  const credit = Number(o.credit_amount_eur || base + bonus);
                  const title = (o.title || "").trim();

                  const label = title
                    ? `${title}`
                    : `Paghi €${base.toFixed(0)} → Credito €${credit.toFixed(
                        0,
                      )}`;

                  return (
                    <option key={o.id} value={String(o.id)}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>

          <button
            type="submit"
            className="cp-topup-button"
            disabled={topupBusy || walletLoading}
          >
            {topupBusy ? "Avvio…" : "Ricarica credito"}
          </button>
        </form>
      </section>

      <section className="cp-card cp-movements-card">
        <h2>Ultimi movimenti</h2>

        {wallet.transactions.length === 0 ? (
          <p className="cp-muted">Nessun movimento registrato.</p>
        ) : (
          <ul className="cp-wallet-list">
            {wallet.transactions.map((t) => {
              const amount = Number(t.amount_cents || 0) / 100;
              const sign = amount >= 0 ? "+" : "−";
              const dt = t.created_at ? new Date(t.created_at) : null;

              const labelType =
                t.type === "topup"
                  ? "Ricarica"
                  : t.type === "spend"
                  ? "Pagamento"
                  : t.type === "refund"
                  ? "Rimborso"
                  : "Bonus";

              return (
                <li key={t.id} className="cp-wallet-row">
                  <span className="cp-wallet-main">
                    <strong>{labelType}</strong>
                    <small>{dt ? dt.toLocaleDateString("it-IT") : "—"}</small>
                  </span>
                  <span
                    className={
                      amount >= 0 ? "cp-wallet-plus" : "cp-wallet-minus"
                    }
                  >
                    {sign}{" "}
                    {Math.abs(amount).toLocaleString("it-IT", {
                      style: "currency",
                      currency: "EUR",
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
